/**
 * Notizen anpinnen / Pin entfernen (wie CRM 1.0, TasksNotes.js).
 *
 * Rechte wie CRM 1.0 (`checkScope('pinNote')`):
 *   - Administrator (Rolle 1) darf immer.
 *   - Mitarbeiter (Rolle 2) nur eigene Notizen (notice.admin_id).
 *   - Ist der PB verstorben (todestag gesetzt), nur noch Administrator.
 *
 * Gepinnte Notizen stehen oben (sortNotices) und sind hervorgehoben.
 */
import { useCallback } from 'react';
import { useOptionalAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionsContext';

export const ROLE_ADMINISTRATOR = 1;
export const ROLE_MITARBEITER = 2;

export interface PinnableNotice {
  admin_id?: number | null;
  pinned?: number | boolean | null;
}

export function canPinNotice(
  notice: PinnableNotice,
  ctx: { roleId: number | null | undefined; adminId: number | null | undefined; isDead: boolean },
): boolean {
  if (ctx.roleId === ROLE_ADMINISTRATOR) return true;
  if (ctx.isDead) return false;
  return ctx.roleId === ROLE_MITARBEITER
    && ctx.adminId != null
    && notice.admin_id != null
    && Number(notice.admin_id) === Number(ctx.adminId);
}

export function isPinned(notice: PinnableNotice): boolean {
  return notice.pinned === true || Number(notice.pinned) === 1;
}

/** Prüft-Funktion für die aktuell angemeldete Person. */
export function useCanPinNotice(isDead: boolean): (notice: PinnableNotice) => boolean {
  const { role } = usePermissions();
  const user = useOptionalAuth()?.user ?? null;
  const roleId = role?.id ?? null;
  const adminId = user?.id ?? null;
  return useCallback(
    (notice: PinnableNotice) => canPinNotice(notice, { roleId, adminId, isDead }),
    [roleId, adminId, isDead],
  );
}

/** PB verstorben? (CRM 1.0: `user.todestag`) */
export function isDeceased(user: unknown): boolean {
  const t = (user as { todestag?: string | null } | null)?.todestag;
  return typeof t === 'string' && t.trim() !== '';
}
